import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { Html5Qrcode } from "html5-qrcode";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

const S = {
  page: { minHeight: "100vh", background: "#F4F2EC", fontFamily: "'Archivo', sans-serif", color: "#0A0A0F" },
  card: { width: "100%", maxWidth: 480, margin: "0 auto", background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: "32px 24px", textAlign: "center" },
  input: { width: "100%", padding: "14px 16px", borderRadius: 0, border: "2px solid #0A0A0F", background: "#FFFFFF", color: "#0A0A0F", fontSize: 16, fontFamily: "'Archivo', sans-serif", outline: "none" },
  btn: (bg, color) => ({ width: "100%", padding: "16px 20px", borderRadius: 0, border: "none", background: bg, color, fontSize: 18, fontWeight: 700, cursor: "pointer", fontFamily: "'Archivo', sans-serif" }),
  tab: (active) => ({ flex: 1, padding: "12px 0", border: "none", borderBottom: active ? "2px solid #0033FF" : "2px solid transparent", background: "transparent", color: active ? "#0033FF" : "rgba(10,10,15,0.58)", fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "'Archivo', sans-serif" }),
};

// ─── QR Scanner ───────────────────────────────────────
function QRScanner({ onScan, onClose }) {
  const scannerRef = useRef(null);
  const stoppedRef = useRef(false);
  const idRef = useRef("qr-reader-" + Math.random().toString(36).slice(2));

  useEffect(() => {
    let mounted = true;
    const scannerId = idRef.current;
    // Wait for DOM element to be ready
    const timer = setTimeout(() => {
      const el = document.getElementById(scannerId);
      if (!el || !mounted) return;
      const scanner = new Html5Qrcode(scannerId);
      scannerRef.current = scanner;
      scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        (text) => {
          if (stoppedRef.current) return;
          stoppedRef.current = true;
          scanner.stop().then(() => {
            if (mounted) onScan(text);
          }).catch(() => {
            if (mounted) onScan(text);
          });
        },
        () => {}
      ).catch((err) => { console.log("Camera error:", err); });
    }, 300);

    return () => {
      mounted = false;
      clearTimeout(timer);
      if (scannerRef.current && !stoppedRef.current) {
        stoppedRef.current = true;
        scannerRef.current.stop().catch(() => {});
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return ReactDOM.createPortal(
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, background: "#F4F2EC", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "2px solid #0A0A0F" }}>
        <span style={{ color: "#0A0A0F", fontSize: 18, fontWeight: 700, fontFamily: "'Archivo', sans-serif" }}>Scan QR Code</span>
        <button onClick={() => {
          if (scannerRef.current && !stoppedRef.current) {
            stoppedRef.current = true;
            scannerRef.current.stop().catch(() => {});
          }
          onClose();
        }} style={{ background: "none", border: "1px solid #ef4444", borderRadius: 0, padding: "8px 20px", color: "#ef4444", fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "'Archivo', sans-serif" }}>
          Cancel
        </button>
      </div>
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div id={idRef.current} style={{ width: "100%", maxWidth: 400 }} />
      </div>
      <div style={{ padding: "16px 20px", textAlign: "center", color: "rgba(10,10,15,0.58)", fontSize: 14 }}>
        Point your camera at a product QR code
      </div>
    </div>,
    document.body
  );
}

// Source labels for display
const SOURCE_LABELS = {
  designshop: "Design Shop",
  vibratovault: "Vibrato Vault",
  "hq-materials": "HQ Materials",
  "hq-filament": "HQ Filament",
  "hq-hardware": "HQ Hardware",
  "hq-shipping": "HQ Shipping",
  "ds-hardware": "DS Hardware",
  "ds-shipping": "DS Shipping",
  "vv-hardware": "VV Hardware",
  "vv-shipping": "VV Shipping",
};
const SOURCE_COLORS = {
  designshop: "#3b82f6",
  vibratovault: "#8b5cf6",
  "hq-materials": "#0033FF",
  "hq-filament": "#0033FF",
  "hq-hardware": "#f59e0b",
  "hq-shipping": "#14b8a6",
  "ds-hardware": "#3b82f6",
  "ds-shipping": "#3b82f6",
  "vv-hardware": "#8b5cf6",
  "vv-shipping": "#8b5cf6",
};

// ─── My Hours Popup ───────────────────────────────────
function getWeekStart(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff)).toISOString().slice(0, 10);
}

// Apply FLSA overtime: anything over 40 hours in a workweek is paid at 1.5×.
// Group entries by week so OT is calculated per week, then summed.
function calcPay(entries, rate) {
  const r = rate || 0;
  const weeks = {};
  entries.forEach((e) => {
    const wk = getWeekStart(e.date);
    if (!weeks[wk]) weeks[wk] = 0;
    weeks[wk] += e.hoursWorked || 0;
  });
  let totalHours = 0, regHours = 0, otHours = 0, gross = 0;
  Object.values(weeks).forEach((wkHours) => {
    const reg = Math.min(wkHours, 40);
    const ot = Math.max(0, wkHours - 40);
    totalHours += wkHours;
    regHours += reg;
    otHours += ot;
    gross += reg * r + ot * r * 1.5;
  });
  return { totalHours, regHours, otHours, gross };
}

function MyHoursPopup({ employee, onClose }) {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API_URL}/api/timeclock/entries?employeeId=${employee.empId}`)
      .then((r) => r.json())
      .then((data) => setEntries(Array.isArray(data) ? data : []))
      .catch(() => setError("Could not load hours"));
  }, [employee.empId]);

  const rate = employee.rate || 0;
  const todayStr = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD local
  const thisWeekKey = getWeekStart(todayStr);
  const monthStart = todayStr.slice(0, 7) + "-01";
  const yearStart = todayStr.slice(0, 4) + "-01-01";

  const all = entries || [];
  const todayEntries = all.filter((e) => e.date === todayStr);
  const weekEntries = all.filter((e) => getWeekStart(e.date) === thisWeekKey);
  const monthEntries = all.filter((e) => e.date >= monthStart);
  const yearEntries = all.filter((e) => e.date >= yearStart);
  const recent = all.slice(0, 10);

  // Today: pay at base rate (OT is calculated weekly, so a single day shows straight-time earned).
  const todayHours = todayEntries.reduce((s, e) => s + (e.hoursWorked || 0), 0);
  const todayPay = todayHours * rate;
  const week = calcPay(weekEntries, rate);
  const month = calcPay(monthEntries, rate);
  const ytd = calcPay(yearEntries, rate);

  const fmtH = (h) => (h || 0).toFixed(2);
  const fmt$ = (v) => `$${(v || 0).toFixed(2)}`;
  const fmtDate = (d) => {
    try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }); }
    catch { return d; }
  };

  const monthLabel = new Date(todayStr + "T00:00:00").toLocaleDateString("en-US", { month: "long" });

  const Row = ({ label, sublabel, hours, pay, breakdown }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, marginBottom: 8 }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#0A0A0F" }}>{label}</div>
        {sublabel && <div style={{ fontSize: 11, color: "rgba(10,10,15,0.58)", marginTop: 2 }}>{sublabel}</div>}
        {breakdown && <div style={{ fontSize: 11, color: "rgba(10,10,15,0.58)", marginTop: 2, fontFamily: "'JetBrains Mono', monospace" }}>{breakdown}</div>}
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700, color: "#0033FF" }}>{fmt$(pay)}</div>
        <div style={{ fontSize: 11, color: "rgba(10,10,15,0.58)", fontFamily: "'JetBrains Mono', monospace" }}>{fmtH(hours)} hrs</div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={onClose} style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)" }} />
      <div style={{ ...S.card, background: "#FFFFFF", position: "relative", zIndex: 301, maxHeight: "85vh", overflowY: "auto", textAlign: "left" }}>
        <h3 style={{ fontSize: 20, fontWeight: 700, color: "#0A0A0F", marginBottom: 4, textAlign: "center" }}>My Hours & Pay</h3>
        <p style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", marginBottom: 16, textAlign: "center" }}>
          {employee.name} · <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmt$(rate)}/hr</span>
        </p>

        {entries === null && !error && <div style={{ textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 20 }}>Loading...</div>}
        {error && <div style={{ color: "#ef4444", fontSize: 14, textAlign: "center", marginBottom: 12 }}>{error}</div>}

        {entries !== null && (
          <>
            <Row label="Today" sublabel={fmtDate(todayStr)} hours={todayHours} pay={todayPay} />
            <Row
              label="This Week"
              sublabel="Mon – Sun"
              hours={week.totalHours}
              pay={week.gross}
              breakdown={week.otHours > 0 ? `${fmtH(week.regHours)} reg + ${fmtH(week.otHours)} OT (1.5×)` : null}
            />
            <Row
              label={`This Month — ${monthLabel}`}
              hours={month.totalHours}
              pay={month.gross}
              breakdown={month.otHours > 0 ? `${fmtH(month.regHours)} reg + ${fmtH(month.otHours)} OT (1.5×)` : null}
            />

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", marginBottom: 16, marginTop: 4, borderTop: "2px solid #0A0A0F", paddingTop: 12 }}>
              <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", textTransform: "uppercase", letterSpacing: 1 }}>Year to Date ({todayStr.slice(0, 4)})</div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 700, color: "#34d399" }}>{fmt$(ytd.gross)}</div>
                <div style={{ fontSize: 11, color: "rgba(10,10,15,0.58)", fontFamily: "'JetBrains Mono', monospace" }}>{fmtH(ytd.totalHours)} hrs</div>
              </div>
            </div>

            <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "rgba(10,10,15,0.58)", marginBottom: 8 }}>Recent Shifts</div>
            {recent.length === 0 ? (
              <div style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", textAlign: "center", padding: 20 }}>No shifts yet</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
                {recent.map((e) => (
                  <div key={e.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#0A0A0F", display: "flex", alignItems: "center", gap: 6 }}>
                        {fmtDate(e.date)}
                        {e.account && e.account !== "nycmfg" && (
                          <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", background: "#0033FF", color: "#fff", fontFamily: "'JetBrains Mono', monospace", textTransform: "uppercase", letterSpacing: "0.04em" }}>{e.account}</span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)" }}>
                        {e.clockIn || "—"} → {e.clockOut || (e.action === "clock_out" ? "—" : "in progress")}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15, fontWeight: 700, color: "#34d399" }}>{fmt$((e.hoursWorked || 0) * rate)}</div>
                      <div style={{ fontSize: 11, color: "rgba(10,10,15,0.58)", fontFamily: "'JetBrains Mono', monospace" }}>{fmtH(e.hoursWorked)} hrs</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

          </>
        )}

        <button onClick={onClose} style={{ ...S.btn("transparent", "rgba(10,10,15,0.58)"), border: "2px solid #0A0A0F", fontSize: 15, padding: "12px 20px" }}>Close</button>
      </div>
    </div>,
    document.body
  );
}

// ─── Action Chooser Popup ─────────────────────────────
function ActionChooserPopup({ product, onRequestRestock, onAddInventory, onClose }) {
  const sourceLabel = SOURCE_LABELS[product._source] || product._source;
  const sourceColor = SOURCE_COLORS[product._source] || "rgba(10,10,15,0.58)";
  const currentStock = product.quantity != null ? product.quantity : "—";
  return ReactDOM.createPortal(
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={onClose} style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)" }} />
      <div style={{ ...S.card, position: "relative", zIndex: 301 }}>
        <h3 style={{ fontSize: 20, fontWeight: 700, color: "#0A0A0F", marginBottom: 4 }}>{product.name}</h3>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 4 }}>{product.sku ? product.sku : ""}</p>
        <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 0, fontSize: 12, fontWeight: 600, background: `${sourceColor}20`, color: sourceColor, border: `1px solid ${sourceColor}30`, marginBottom: 12 }}>{sourceLabel}</span>
        <div style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 20 }}>
          Current Stock: <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#0A0A0F" }}>{currentStock}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <button onClick={onAddInventory} style={{
            ...S.btn("#22c55e", "#fff"), display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Inventory
          </button>
          <button onClick={onRequestRestock} style={{
            ...S.btn("#0033FF", "#FFFFFF"), display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 11-6.219-8.56"/><polyline points="21 3 21 9 15 9"/></svg>
            Request Restock
          </button>
          <button onClick={onClose} style={{ ...S.btn("transparent", "rgba(10,10,15,0.58)"), border: "2px solid #0A0A0F", fontSize: 15, padding: "12px 20px" }}>Cancel</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Add Inventory Popup ──────────────────────────────
function AddInventoryPopup({ product, onSubmit, onClose }) {
  const [qty, setQty] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const sourceLabel = SOURCE_LABELS[product._source] || product._source;
  const sourceColor = SOURCE_COLORS[product._source] || "rgba(10,10,15,0.58)";
  const currentStock = product.quantity != null ? product.quantity : "—";

  const handleSubmit = () => {
    if (!qty || parseInt(qty) <= 0) return;
    setSubmitting(true);
    onSubmit(parseInt(qty), notes);
  };

  return ReactDOM.createPortal(
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={onClose} style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)" }} />
      <div style={{ ...S.card, position: "relative", zIndex: 301 }}>
        <h3 style={{ fontSize: 20, fontWeight: 700, color: "#0A0A0F", marginBottom: 4 }}>Add Inventory</h3>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 4 }}>{product.name} {product.sku ? `(${product.sku})` : ""}</p>
        <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 0, fontSize: 12, fontWeight: 600, background: `${sourceColor}20`, color: sourceColor, border: `1px solid ${sourceColor}30`, marginBottom: 12 }}>{sourceLabel}</span>
        <div style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 16 }}>
          Current Stock: <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#0A0A0F" }}>{currentStock}</span>
        </div>
        <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} min="1" placeholder="Quantity received" autoFocus
          style={{ ...S.input, textAlign: "center", fontSize: 20, marginBottom: 10 }}
          onKeyDown={(e) => { if (e.key === "Enter" && qty) handleSubmit(); }} />
        <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)"
          style={{ ...S.input, textAlign: "center", fontSize: 14, marginBottom: 16 }}
          onKeyDown={(e) => { if (e.key === "Enter" && qty) handleSubmit(); }} />
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ ...S.btn("transparent", "rgba(10,10,15,0.58)"), border: "2px solid #0A0A0F", fontSize: 15 }}>Cancel</button>
          <button onClick={handleSubmit} disabled={submitting || !qty} style={{ ...S.btn("#22c55e", "#fff"), fontSize: 15, opacity: submitting || !qty ? 0.6 : 1 }}>
            {submitting ? "Adding..." : "Add"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Request Popup ────────────────────────────────────
const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const PRIORITY_COLORS = {
  Low: { bg: "rgba(90,138,181,0.1)", border: "rgba(10,10,15,0.58)", text: "rgba(10,10,15,0.58)" },
  Medium: { bg: "rgba(200,149,108,0.1)", border: "#0033FF", text: "#0033FF" },
  High: { bg: "rgba(249,115,22,0.1)", border: "#f97316", text: "#f97316" },
  Urgent: { bg: "rgba(239,68,68,0.15)", border: "#ef4444", text: "#ef4444" },
};

function RequestPopup({ product, onSubmit, onClose }) {
  const [qty, setQty] = useState("");
  const [notes, setNotes] = useState("");
  const [priority, setPriority] = useState("Medium");
  const sourceLabel = SOURCE_LABELS[product._source] || product._source;
  const sourceColor = SOURCE_COLORS[product._source] || "rgba(10,10,15,0.58)";
  return ReactDOM.createPortal(
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={onClose} style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)" }} />
      <div style={{ ...S.card, position: "relative", zIndex: 301 }}>
        <h3 style={{ fontSize: 20, fontWeight: 700, color: "#0A0A0F", marginBottom: 4 }}>Request Restock</h3>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 4 }}>{product.name} {product.sku ? `(${product.sku})` : ""}</p>
        <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 0, fontSize: 12, fontWeight: 600, background: `${sourceColor}20`, color: sourceColor, border: `1px solid ${sourceColor}30`, marginBottom: 16 }}>{sourceLabel}</span>
        <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} min="1" placeholder="Quantity needed" autoFocus
          style={{ ...S.input, textAlign: "center", fontSize: 20, marginBottom: 10 }}
          onKeyDown={(e) => { if (e.key === "Enter" && qty) onSubmit(parseInt(qty), notes, priority); }} />
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Priority</div>
          <div style={{ display: "flex", gap: 6 }}>
            {PRIORITIES.map((p) => {
              const pc = PRIORITY_COLORS[p];
              const active = priority === p;
              return (
                <button key={p} onClick={() => setPriority(p)} style={{
                  flex: 1, padding: "10px 4px", borderRadius: 0, fontSize: 13, fontWeight: 600, cursor: "pointer",
                  fontFamily: "'Archivo', sans-serif", transition: "all .15s",
                  border: `1px solid ${active ? pc.border : "#0A0A0F"}`,
                  background: active ? pc.bg : "transparent",
                  color: active ? pc.text : "rgba(10,10,15,0.58)",
                }}>{p}</button>
              );
            })}
          </div>
        </div>
        <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)"
          style={{ ...S.input, textAlign: "center", fontSize: 14, marginBottom: 16 }}
          onKeyDown={(e) => { if (e.key === "Enter" && qty) onSubmit(parseInt(qty), notes, priority); }} />
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ ...S.btn("transparent", "rgba(10,10,15,0.58)"), border: "2px solid #0A0A0F", fontSize: 15 }}>Cancel</button>
          <button onClick={() => qty && onSubmit(parseInt(qty), notes, priority)} style={{ ...S.btn("#0033FF", "#FFFFFF"), fontSize: 15 }}>Submit</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Main Field App ───────────────────────────────────
export default function FieldApp() {
  const [employee, setEmployee] = useState(() => {
    try {
      const s = sessionStorage.getItem("field-user");
      if (s) return JSON.parse(s);
      // Auto-login from main app session
      const main = sessionStorage.getItem("nycmfg-user");
      if (main && sessionStorage.getItem("nycmfg-auth") === "true") {
        const emp = JSON.parse(main);
        sessionStorage.setItem("field-user", main);
        return emp;
      }
      return null;
    } catch { return null; }
  });
  const [empId, setEmpId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // After login
  const [tab, setTabRaw] = useState(() => sessionStorage.getItem("field-tab") || "clock");
  const setTab = (t) => { setTabRaw(t); sessionStorage.setItem("field-tab", t); };
  const [status, setStatus] = useState(null);
  const [clockMsg, setClockMsg] = useState("");
  const [store, setStore] = useState(() => sessionStorage.getItem("field-store") || ""); // eslint-disable-line no-unused-vars
  const [allItems, setAllItems] = useState([]); // All scannable items across all sources
  const [scanning, setScanning] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null); // Triggers ActionChooser
  const [requestProduct, setRequestProduct] = useState(null);
  const [addInventoryProduct, setAddInventoryProduct] = useState(null);
  const [search, setSearch] = useState("");
  const [myActivity, setMyActivity] = useState([]);
  const [toastMsg, setToastMsg] = useState(null); // { text, color }
  const [showHours, setShowHours] = useState(false);
  // Time-clock billing accounts (nycmfg + client brands like Darkfin Labs).
  const [accounts, setAccounts] = useState([{ key: "nycmfg", label: "NYC MFG" }]);
  const [selectedAccount, setSelectedAccount] = useState("nycmfg");

  const showToast = (text, color) => {
    setToastMsg({ text, color });
    setTimeout(() => setToastMsg(null), 4000);
  };

  // Get stores employee has access to
  const getStores = (perms) => {
    const stores = [];
    const p = perms || [];
    if (p.some((x) => x.startsWith("ds-")) || p.some((x) => x.startsWith("store-"))) {
      stores.push({ key: "designshop", label: "Design Shop" });
    }
    if (p.some((x) => x.startsWith("vv-")) || p.some((x) => x.startsWith("store-"))) {
      stores.push({ key: "vibratovault", label: "Vibrato Vault" });
    }
    return stores;
  };

  const login = () => {
    setLoading(true); setError("");
    fetch(`${API_URL}/api/employees/login`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ empId, password }),
    }).then((r) => r.json()).then((data) => {
      setLoading(false);
      if (data.success) {
        setEmployee(data.employee);
        sessionStorage.setItem("field-user", JSON.stringify(data.employee));
        const stores = getStores(data.employee.permissions);
        if (stores.length > 0) { setStore(stores[0].key); sessionStorage.setItem("field-store", stores[0].key); }
        fetchStatus(data.employee.empId);
      } else setError(data.error || "Invalid credentials");
    }).catch(() => { setLoading(false); setError("Connection error"); });
  };

  const fetchStatus = (eid) => {
    fetch(`${API_URL}/api/timeclock/status/${eid}`).then((r) => r.json()).then((d) => {
      setStatus(d.status);
      if (d.entry && d.entry.account) setSelectedAccount(d.entry.account);
    }).catch(() => {});
  };

  const punch = (action, account = selectedAccount) => {
    setLoading(true);
    fetch(`${API_URL}/api/timeclock/punch`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ employeeId: employee.empId, employeeName: employee.name, action, account }),
    }).then((r) => r.json()).then((d) => {
      setLoading(false);
      if (d.error) setClockMsg(d.error);
      else { setClockMsg(d.message); fetchStatus(employee.empId); }
      setTimeout(() => setClockMsg(""), 4000);
    }).catch(() => { setLoading(false); });
  };

  // Accounts this employee may punch into: home account plus any assigned brands.
  const myAccounts = accounts.filter((a) => a.key === "nycmfg" || (employee?.clockAccounts || []).includes(a.key));

  // Fetch all scannable items across stores + HQ (all from Postgres API)
  const fetchAllItems = useCallback(() => {
    Promise.all([
      fetch(`${API_URL}/api/products?store=designshop`).then((r) => r.json()).catch(() => []),
      fetch(`${API_URL}/api/products?store=vibratovault`).then((r) => r.json()).catch(() => []),
      fetch(`${API_URL}/api/materials`).then((r) => r.json()).catch(() => []),
      fetch(`${API_URL}/api/filament`).then((r) => r.json()).catch(() => []),
      fetch(`${API_URL}/api/hardware?location=nycmfg`).then((r) => r.json()).catch(() => []),
      fetch(`${API_URL}/api/shipping-supplies?location=nycmfg`).then((r) => r.json()).catch(() => []),
      fetch(`${API_URL}/api/hardware?location=designshop`).then((r) => r.json()).catch(() => []),
      fetch(`${API_URL}/api/hardware?location=vibratovault`).then((r) => r.json()).catch(() => []),
      fetch(`${API_URL}/api/shipping-supplies?location=designshop`).then((r) => r.json()).catch(() => []),
      fetch(`${API_URL}/api/shipping-supplies?location=vibratovault`).then((r) => r.json()).catch(() => []),
    ]).then(([dsProd, vvProd, materials, filament, hqHw, hqSh, dsHw, vvHw, dsSh, vvSh]) => {
      const items = [];
      (Array.isArray(dsProd) ? dsProd : []).forEach((p) => items.push({ ...p, _source: "designshop", _type: "inventory" }));
      (Array.isArray(vvProd) ? vvProd : []).forEach((p) => items.push({ ...p, _source: "vibratovault", _type: "inventory" }));
      (Array.isArray(materials) ? materials : []).forEach((m) => items.push({ id: m.id, name: `${m.type || ""} ${m.color || ""} ${m.sheetSize || ""} ${m.thickness || ""}`.trim(), sku: "", quantity: m.quantity || "0", _source: "hq-materials", _type: "hardware" }));
      (Array.isArray(filament) ? filament : []).forEach((f) => items.push({ id: f.id, name: `${f.material || ""} ${f.brand || ""} ${f.color || ""}`.trim(), sku: f.sku || "", quantity: f.quantity || "0", _source: "hq-filament", _type: "hardware" }));
      (Array.isArray(hqHw) ? hqHw : []).forEach((h) => items.push({ ...h, _source: "hq-hardware", _type: "hardware" }));
      (Array.isArray(hqSh) ? hqSh : []).forEach((s) => items.push({ ...s, _source: "hq-shipping", _type: "hardware" }));
      (Array.isArray(dsHw) ? dsHw : []).forEach((h) => items.push({ ...h, _source: "ds-hardware", _type: "hardware" }));
      (Array.isArray(vvHw) ? vvHw : []).forEach((h) => items.push({ ...h, _source: "vv-hardware", _type: "hardware" }));
      (Array.isArray(dsSh) ? dsSh : []).forEach((s) => items.push({ ...s, _source: "ds-shipping", _type: "hardware" }));
      (Array.isArray(vvSh) ? vvSh : []).forEach((s) => items.push({ ...s, _source: "vv-shipping", _type: "hardware" }));
      setAllItems(items);
    });
  }, []);

  const fetchMyActivity = useCallback(() => {
    if (!employee) return;
    Promise.all([
      // Restock requests
      fetch(`${API_URL}/api/restock?store=designshop`).then((r) => r.json()).catch(() => []),
      fetch(`${API_URL}/api/restock?store=vibratovault`).then((r) => r.json()).catch(() => []),
      // Inventory log
      fetch(`${API_URL}/api/inventory/log?addedBy=${encodeURIComponent(employee.name)}&limit=10`).then((r) => r.json()).catch(() => []),
    ]).then(([ds, vv, logs]) => {
      const requests = [...(Array.isArray(ds) ? ds : []), ...(Array.isArray(vv) ? vv : [])]
        .filter((r) => r.requestedBy === employee.name)
        .map((r) => ({ ...r, _activityType: "request", _sortDate: r.createdAt || "" }));
      const additions = (Array.isArray(logs) ? logs : [])
        .map((l) => ({ ...l, _activityType: "addition", _sortDate: l.createdAt || "" }));
      const merged = [...requests, ...additions]
        .sort((a, b) => (b._sortDate > a._sortDate ? 1 : -1))
        .slice(0, 15);
      setMyActivity(merged);
    });
  }, [employee]);

  useEffect(() => { fetchAllItems(); fetchMyActivity(); }, [fetchAllItems, fetchMyActivity]);

  // Restore status on mount if already logged in
  useEffect(() => { if (employee) fetchStatus(employee.empId); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetch(`${API_URL}/api/clock-accounts`).then((r) => r.json()).then((d) => { if (Array.isArray(d) && d.length) setAccounts(d); }).catch(() => {});
  }, []);

  const handleQRScan = (text) => {
    setScanning(false);
    setTimeout(() => {
      const raw = text.trim();
      // Check for base product QR code (BP: prefix)
      if (raw.startsWith("BP:")) {
        const baseName = raw.slice(3);
        const item = allItems.find((p) => p.baseProduct && p.baseProduct.toLowerCase() === baseName.toLowerCase());
        if (item) {
          setSelectedProduct({ ...item, name: baseName, _isBaseProduct: true });
        } else {
          setError(`No base product found: ${baseName}`);
          setTimeout(() => setError(""), 4000);
        }
        return;
      }
      // Regular SKU scan
      const item = allItems.find((p) => p.sku && p.sku.toLowerCase() === raw.toLowerCase());
      if (item) {
        setSelectedProduct(item);
      } else {
        setError(`No item found for SKU: ${raw}`);
        setTimeout(() => setError(""), 4000);
      }
    }, 200);
  };

  const submitRequest = (qty, notes, priority) => {
    const source = requestProduct._source;
    const type = requestProduct._type || "inventory";
    let targetStore = null;
    if (source === "designshop" || source === "ds-hardware" || source === "ds-shipping") targetStore = "designshop";
    else if (source === "vibratovault" || source === "vv-hardware" || source === "vv-shipping") targetStore = "vibratovault";
    else targetStore = "designshop";

    const newRequest = {
      id: String(Date.now()),
      name: requestProduct.name,
      quantity: String(qty),
      notes: notes || "",
      sku: requestProduct.sku || "",
      priority: priority || "Medium",
      status: "Requested",
      type: type,
      source: source,
      requestedBy: employee.name,
      date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
    };

    // Save to store-requests-v1 in localStorage (kanban board)
    try {
      const existing = JSON.parse(localStorage.getItem("store-requests-v1") || "{}");
      const storeReqs = existing[targetStore] || [];
      storeReqs.push(newRequest);
      existing[targetStore] = storeReqs;
      localStorage.setItem("store-requests-v1", JSON.stringify(existing));
    } catch (e) { /* ignore */ }

    // Also submit to existing restock API for store products
    if (source === "designshop" || source === "vibratovault") {
      fetch(`${API_URL}/api/restock`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ store: source, productId: requestProduct.id, productName: requestProduct.name, sku: requestProduct.sku || "", quantityRequested: qty, requestedBy: employee.name }),
      }).catch(() => {});
    }

    setRequestProduct(null);
    const storeLabel = SOURCE_LABELS[targetStore] || targetStore;
    showToast(`Request sent to ${storeLabel}!`, "#34d399");
    fetchMyActivity();
  };

  const submitAddInventory = (qty, notes) => {
    const product = addInventoryProduct;
    fetch(`${API_URL}/api/inventory/add`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        source: product._source,
        itemId: product.id,
        quantity: qty,
        notes: notes || "",
        addedBy: employee.name,
        isBaseProduct: !!product._isBaseProduct,
        baseProductName: product._isBaseProduct ? product.name : null,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        setAddInventoryProduct(null);
        if (data.success) {
          showToast(`+${qty} added to ${data.itemName} (${data.quantityBefore} → ${data.quantityAfter})`, "#34d399");
          fetchAllItems();
          fetchMyActivity();
        } else {
          showToast(data.error || "Failed to add inventory", "#ef4444");
        }
      })
      .catch(() => {
        setAddInventoryProduct(null);
        showToast("Connection error — inventory not updated", "#ef4444");
      });
  };

  const filtered = search ? allItems.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()) || (p.sku || "").toLowerCase().includes(search.toLowerCase()) || (p.baseProduct || "").toLowerCase().includes(search.toLowerCase())).slice(0, 20) : [];

  const logout = () => { sessionStorage.removeItem("field-user"); sessionStorage.removeItem("field-store"); sessionStorage.removeItem("field-tab"); setEmployee(null); setEmpId(""); setPassword(""); setStatus(null); setStore(""); };

  // ─── Login Screen ──────────────────────────────────
  if (!employee) {
    return (
      <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;700&family=DM+Sans:wght@400;500;600;700&display=swap');*{box-sizing:border-box;margin:0;padding:0}body{background:F4F2EC}`}</style>
        <div style={S.card}>
          <h1 style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.2, background: "#0033FF", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", marginBottom: 4 }}>NYC Manufacturing</h1>
          <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 28 }}>Field App</p>
          <div style={{ marginBottom: 16, textAlign: "left" }}>
            <label style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 6, display: "block" }}>Employee ID</label>
            <input value={empId} onChange={(e) => setEmpId(e.target.value)} placeholder="e.g. 01" style={{ ...S.input, textAlign: "center", letterSpacing: 4, fontFamily: "'JetBrains Mono', monospace", fontSize: 20 }}
              onKeyDown={(e) => { if (e.key === "Enter") document.getElementById("field-pw").focus(); }} />
          </div>
          <div style={{ marginBottom: 24, textAlign: "left" }}>
            <label style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 6, display: "block" }}>Password</label>
            <input id="field-pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" style={{ ...S.input, textAlign: "center" }}
              onKeyDown={(e) => { if (e.key === "Enter") login(); }} />
          </div>
          {error && <div style={{ color: "#ef4444", fontSize: 14, marginBottom: 12 }}>{error}</div>}
          <button onClick={login} disabled={loading} style={{ ...S.btn("#0033FF", "#FFFFFF"), opacity: loading ? 0.7 : 1 }}>
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </div>
      </div>
    );
  }

  // ─── Main App ──────────────────────────────────────
  const stores = getStores(employee.permissions); // eslint-disable-line no-unused-vars

  return (
    <div style={S.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;700&family=DM+Sans:wght@400;500;600;700&display=swap');*{box-sizing:border-box;margin:0;padding:0}body{background:F4F2EC}input{font-size:16px!important;}`}</style>

      {/* Header */}
      <div style={{ padding: "16px 20px", borderBottom: "2px solid #0A0A0F", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#0A0A0F" }}>Hello, {employee.name}!</div>
          <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)" }}>Field App</div>
        </div>
        <button onClick={logout} style={{ background: "none", border: "1px solid #ef4444", borderRadius: 0, padding: "5px 12px", color: "#ef4444", fontSize: 13, cursor: "pointer", fontFamily: "'Archivo', sans-serif" }}>Sign Out</button>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: "2px solid #0A0A0F" }}>
        <button onClick={() => setTab("clock")} style={S.tab(tab === "clock")}>Time Clock</button>
        <button onClick={() => setTab("inventory")} style={S.tab(tab === "inventory" || tab === "requests")}>Inventory</button>
      </div>

      <div style={{ padding: 20 }}>
        {/* ─── Time Clock Tab ─── */}
        {tab === "clock" && (
          <div style={{ maxWidth: 400, margin: "0 auto" }}>
            {status && status !== "not_clocked_in" && (
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <span style={{
                  display: "inline-block", padding: "6px 16px", borderRadius: 0, fontSize: 14, fontWeight: 600,
                  background: status === "clock_in" ? "#22c55e20" : status === "post_office" ? "#f59e0b20" : "#34d39920",
                  color: status === "clock_in" ? "#22c55e" : status === "post_office" ? "#f59e0b" : "#34d399",
                  border: `1px solid ${status === "clock_in" ? "#22c55e30" : status === "post_office" ? "#f59e0b30" : "#34d39930"}`,
                }}>
                  {status === "clock_in" ? "Clocked In" : status === "post_office" ? "At Post Office" : "Clocked Out"}
                </span>
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {/* NYC MFG ("Clock In") plus a button per assigned client brand
                  (e.g. Darkfin Labs). Tapping one switches the open session. */}
              {myAccounts.map((a) => {
                const isNyc = a.key === "nycmfg";
                const isClockedIn = status === "clock_in";
                const isOnPO = status === "post_office";
                const active = isClockedIn && selectedAccount === a.key;
                // NYC MFG ("Clock In") only starts a session. A client account
                // only switches in while working — which clocks NYC MFG out.
                // The next Post Office / Clock Out then ends the client time.
                const enabled = isNyc
                  ? !(isClockedIn || isOnPO)
                  : (isClockedIn && selectedAccount !== a.key);
                const bg = !enabled ? "#F4F2EC" : (isNyc ? "#22c55e" : "#0A0A0F");
                const fg = !enabled ? "rgba(10,10,15,0.58)" : "#fff";
                const labelTxt = isNyc
                  ? (active ? "Clocked In" : isClockedIn ? "Clocked Out" : isOnPO ? "Clocked In" : "Clock In")
                  : (active ? `On ${a.label}` : a.label);
                return (
                  <button key={a.key} onClick={() => punch("clock_in", a.key)} disabled={loading || !enabled}
                    style={{ ...S.btn(bg, fg), opacity: loading ? 0.6 : 1 }}>{labelTxt}</button>
                );
              })}
              <button onClick={() => punch("post_office")} disabled={loading || status !== "clock_in"} style={{ ...S.btn(status === "clock_in" ? "#3b82f6" : "#F4F2EC", status === "clock_in" ? "#fff" : "rgba(10,10,15,0.58)"), opacity: loading ? 0.6 : 1 }}>{status === "post_office" ? "At Post Office" : "Post Office"}</button>
              <button onClick={() => punch("clock_out")} disabled={loading || (!status || status === "clock_out" || status === "not_clocked_in")} style={{ ...S.btn((status === "clock_in" || status === "post_office") ? "#ef4444" : "#F4F2EC", (status === "clock_in" || status === "post_office") ? "#fff" : "rgba(10,10,15,0.58)"), opacity: loading ? 0.6 : 1 }}>Clock Out</button>
              <button onClick={() => setShowHours(true)} style={{
                ...S.btn("transparent", "#0033FF"),
                border: "1px solid #0033FF",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 4,
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                View Hours
              </button>
            </div>
            {clockMsg && <div style={{ textAlign: "center", marginTop: 16, fontSize: 14, color: clockMsg.includes("error") || clockMsg.includes("Already") ? "#ef4444" : "#34d399" }}>{clockMsg}</div>}
          </div>
        )}

        {/* ─── Inventory Tab ─── */}
        {(tab === "inventory" || tab === "requests") && (
          <div style={{ maxWidth: 480, margin: "0 auto" }}>
            {/* Scan button */}
            <button onClick={() => setScanning(true)} style={{
              ...S.btn("#0033FF", "#FFFFFF"),
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 16,
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 7V5a2 2 0 012-2h2" /><path d="M17 3h2a2 2 0 012 2v2" /><path d="M21 17v2a2 2 0 01-2 2h-2" /><path d="M7 21H5a2 2 0 01-2-2v-2" />
                <rect x="7" y="7" width="10" height="10" rx="1" />
              </svg>
              Scan QR Code
            </button>

            {/* Manual search */}
            <div style={{ marginBottom: 16 }}>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Or search by name / SKU..." style={S.input} />
              {search && filtered.length > 0 && (
                <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, marginTop: 4, maxHeight: 200, overflowY: "auto" }}>
                  {filtered.map((p, i) => (
                    <div key={`${p._source}-${p.id}-${i}`} onClick={() => { setSelectedProduct(p); setSearch(""); }} style={{ padding: "10px 14px", cursor: "pointer", borderBottom: "2px solid #0A0A0F", transition: "background .1s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(22,45,80,0.5)")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ fontSize: 15, fontWeight: 500, color: "#0A0A0F" }}>{p.name}</div>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: "1px 6px", borderRadius: 0, background: `${SOURCE_COLORS[p._source] || "rgba(10,10,15,0.58)"}20`, color: SOURCE_COLORS[p._source] || "rgba(10,10,15,0.58)", flexShrink: 0 }}>
                          {SOURCE_LABELS[p._source] || p._source}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{p.sku || ""}</div>
                    </div>
                  ))}
                </div>
              )}
              {search && filtered.length === 0 && <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", marginTop: 6 }}>No products match</div>}
            </div>

            {error && <div style={{ color: "#ef4444", fontSize: 14, marginBottom: 12, textAlign: "center" }}>{error}</div>}
            {toastMsg && <div style={{ color: toastMsg.color, fontSize: 14, marginBottom: 12, textAlign: "center" }}>{toastMsg.text}</div>}

            {/* Recent activity */}
            {myActivity.length > 0 && (
              <div>
                <h3 style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "rgba(10,10,15,0.58)", marginBottom: 10 }}>Your Recent Activity</h3>
                {myActivity.map((item, idx) => {
                  if (item._activityType === "request") {
                    return (
                      <div key={`req-${item.id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#FFFFFF", borderRadius: 0, border: "2px solid #0A0A0F", marginBottom: 6 }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 500, color: "#0A0A0F" }}>{item.productName}</div>
                          <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)" }}>{item.sku || ""} · {item.createdAt ? new Date(item.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 700, color: "#0033FF" }}>{item.quantityRequested}</div>
                          <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "flex-end" }}>
                            <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 0, background: "rgba(0,51,255,0.12)", color: "#0033FF" }}>Request</span>
                            <span style={{
                              fontSize: 11, fontWeight: 600, padding: "1px 6px", borderRadius: 0,
                              background: item.status === "pending" ? "#f59e0b20" : item.status === "approved" ? "#22c55e20" : "#34d39920",
                              color: item.status === "pending" ? "#f59e0b" : "#22c55e",
                            }}>{item.status}</span>
                          </div>
                        </div>
                      </div>
                    );
                  } else {
                    // Inventory addition
                    return (
                      <div key={`add-${item.id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#FFFFFF", borderRadius: 0, border: "2px solid #0A0A0F", marginBottom: 6 }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 500, color: "#0A0A0F" }}>{item.itemName}</div>
                          <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)" }}>{item.sku || ""} · {item.createdAt ? new Date(item.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 700, color: "#34d399" }}>+{item.quantityAdded}</div>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 0, background: "#22c55e20", color: "#22c55e" }}>Added</span>
                        </div>
                      </div>
                    );
                  }
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* QR Scanner */}
      {scanning && <QRScanner onScan={handleQRScan} onClose={() => setScanning(false)} />}

      {/* Action Chooser Popup */}
      {selectedProduct && !requestProduct && !addInventoryProduct && (
        <ActionChooserPopup
          product={selectedProduct}
          onRequestRestock={() => { setRequestProduct(selectedProduct); setSelectedProduct(null); }}
          onAddInventory={() => { setAddInventoryProduct(selectedProduct); setSelectedProduct(null); }}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      {/* Request Popup */}
      {requestProduct && <RequestPopup product={requestProduct} onSubmit={submitRequest} onClose={() => setRequestProduct(null)} />}

      {/* Add Inventory Popup */}
      {addInventoryProduct && <AddInventoryPopup product={addInventoryProduct} onSubmit={submitAddInventory} onClose={() => setAddInventoryProduct(null)} />}

      {/* My Hours Popup */}
      {showHours && <MyHoursPopup employee={employee} onClose={() => setShowHours(false)} />}
    </div>
  );
}
